import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { postsService } from '../../services/postsService';
import Button from '../ui/Button';

interface PollDisplayProps {
  postId: string;
  pollOptions: string[]; // Poll options stored in mediaUrls
  onVote?: () => void;
}

interface PollResults {
  totalVotes: number;
  options: Array<{ option: string; votes: number; percentage: number }>;
  userVote: number | null;
}

const PollDisplay: React.FC<PollDisplayProps> = ({ postId, pollOptions, onVote }) => {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [pollResults, setPollResults] = useState<PollResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);

  useEffect(() => {
    loadPollResults();
  }, [postId]);

  const loadPollResults = async () => {
    try {
      const results = await postsService.getPollResults(postId);
      setPollResults(results);
    } catch (error) {
      console.error('Failed to load poll results:', error);
      // Set default results if API fails
      setPollResults({
        totalVotes: 0,
        options: pollOptions.map(opt => ({ option: opt, votes: 0, percentage: 0 })),
        userVote: null
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionIndex: number) => {
    if (!isAuthenticated) {
      alert('Please log in to vote');
      return;
    }

    if (voting) return;
    setVoting(true);

    try {
      await postsService.vote(postId, optionIndex);
      await loadPollResults();
      if (onVote) onVote();
    } catch (error: any) {
      console.error('Failed to vote:', error);
      alert(error?.response?.data?.message || 'Failed to vote. Please try again.');
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {pollOptions.map((_, index) => (
          <div key={index} className="h-12 bg-gray-100 rounded-lg animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (!pollResults) {
    return null;
  }

  const hasVoted = pollResults.userVote !== null;
  const totalVotes = pollResults.totalVotes;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-gray-700">
          {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        </p>
        {hasVoted && (
          <span className="text-xs text-gray-500">You voted</span>
        )}
      </div>

      {pollOptions.map((option, index) => {
        const optionResults = pollResults.options[index] || { option, votes: 0, percentage: 0 };
        const isUserVote = pollResults.userVote === index;
        const percentage = optionResults.percentage;

        return (
          <div key={index} className="relative">
            <button
              type="button"
              onClick={() => !hasVoted && handleVote(index)}
              disabled={hasVoted || voting || !isAuthenticated}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                isUserVote
                  ? 'border-[color:var(--patreon-orange)] bg-[rgba(255,107,53,0.05)]'
                  : hasVoted
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-200 bg-white hover:border-[color:var(--patreon-orange)] hover:bg-gray-50 cursor-pointer'
              }`}
              style={isUserVote ? { borderColor: 'var(--patreon-orange)' } : undefined}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0">
                  {hasVoted && (
                    <div className="flex-shrink-0 mr-3">
                      {isUserVote ? (
                        <CheckCircleSolidIcon className="w-5 h-5" style={{ color: 'var(--patreon-orange)' }} />
                      ) : (
                        <CheckCircleIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-900 truncate">{option}</span>
                </div>
                {hasVoted && (
                  <span className="ml-3 text-sm font-semibold text-gray-700 flex-shrink-0">
                    {percentage.toFixed(1)}%
                  </span>
                )}
              </div>

              {hasVoted && totalVotes > 0 && (
                <div className="mt-3">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: isUserVote ? 'var(--patreon-orange)' : '#9CA3AF'
                      }}
                    ></div>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {optionResults.votes} {optionResults.votes === 1 ? 'vote' : 'votes'}
                  </div>
                </div>
              )}
            </button>
          </div>
        );
      })}

      {!isAuthenticated && (
        <p className="text-sm text-center text-gray-500 mt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              window.location.href = '/login';
            }}
          >
            Log in to vote
          </Button>
        </p>
      )}
    </div>
  );
};

export default PollDisplay;

